import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  AlertCircle,
  Download,
  Filter,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { 
  collection, 
  query, 
  where, 
  getDocs,
  onSnapshot,
  orderBy,
  limit,
  startAfter,
  endBefore,
  limitToLast
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useRealtime } from '../../contexts/RealtimeContext';

const TeacherMyAttendance = () => {
  const { currentUser, userProfile } = useRealtime();
  const [attendanceData, setAttendanceData] = useState({
    todayStatus: null,
    monthlyStats: {
      presentDays: 0,
      absentDays: 0,
      halfDays: 0,
      workingDays: 0,
      attendancePercentage: 0
    },
    allAttendance: []
  });
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    if (currentUser?.uid && userProfile?.role === 'teacher') {
      loadAttendanceData();
      setupRealtimeListener();
    }
  }, [currentUser, userProfile, selectedMonth]);

  const setupRealtimeListener = () => {
    if (!currentUser?.uid) return;

    // Listen for real-time updates to teacher attendance
    const attendanceQuery = query(
      collection(db, 'teacherDailyAttendance'),
      where('teacherId', '==', currentUser.uid),
      orderBy('date', 'desc')
    );

    const unsubscribe = onSnapshot(attendanceQuery, (snapshot) => {
      const attendanceRecords = [];
      snapshot.forEach(doc => {
        attendanceRecords.push({ id: doc.id, ...doc.data() });
      });
      
      updateAttendanceStats(attendanceRecords);
    });

    return unsubscribe;
  };

  const loadAttendanceData = async () => {
    try {
      setLoading(true);
      
      // Get all attendance records for the teacher
      const attendanceQuery = query(
        collection(db, 'teacherDailyAttendance'),
        where('teacherId', '==', currentUser.uid),
        orderBy('date', 'desc')
      );
      
      const snapshot = await getDocs(attendanceQuery);
      const attendanceRecords = [];
      
      snapshot.forEach(doc => {
        attendanceRecords.push({ id: doc.id, ...doc.data() });
      });
      
      updateAttendanceStats(attendanceRecords);
      
    } catch (error) {
      console.error('Error loading attendance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateAttendanceStats = (attendanceRecords) => {
    const today = new Date().toISOString().split('T')[0];
    
    // Find today's attendance
    const todayAttendance = attendanceRecords.find(record => record.date === today);
    
    // Filter records for selected month
    const monthlyRecords = attendanceRecords.filter(record => 
      record.date && record.date.startsWith(selectedMonth)
    );
    
    let presentDays = 0;
    let absentDays = 0;
    let halfDays = 0;
    
    monthlyRecords.forEach(record => {
      switch (record.status) {
        case 'present':
          presentDays++;
          break;
        case 'absent':
          absentDays++;
          break;
        case 'half_day':
          halfDays++;
          break;
      }
    });
    
    // Calculate working days for selected month (excluding Sundays)
    const [year, month] = selectedMonth.split('-').map(Number);
    const daysInMonth = new Date(year, month, 0).getDate();
    
    let workingDays = 0;
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month - 1, d);
      if (date.getDay() !== 0) { // Exclude Sundays
        workingDays++;
      }
    }
    
    const totalPresentDays = presentDays + (halfDays * 0.5);
    const attendancePercentage = workingDays > 0 ? Math.round((totalPresentDays / workingDays) * 100) : 0;
    
    setAttendanceData({
      todayStatus: todayAttendance?.status || null,
      monthlyStats: {
        presentDays,
        absentDays,
        halfDays,
        workingDays,
        attendancePercentage
      },
      allAttendance: attendanceRecords
    });
  };

  const getFilteredAttendance = () => {
    let filtered = attendanceData.allAttendance.filter(record => 
      record.date && record.date.startsWith(selectedMonth)
    );

    if (filterStatus !== 'all') {
      filtered = filtered.filter(record => record.status === filterStatus);
    }

    return filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'present':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'absent':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'half_day':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'present':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'absent':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'half_day':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatShortDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const exportAttendance = () => {
    const filteredData = getFilteredAttendance();
    const csvContent = [
      ['Date', 'Status', 'Check In', 'Check Out', 'Hours Worked', 'Notes'],
      ...filteredData.map(record => [
        record.date,
        record.status,
        record.checkInTime || '',
        record.checkOutTime || '',
        record.hoursWorked || '',
        record.notes || ''
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance_${selectedMonth}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const navigateMonth = (direction) => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const newDate = new Date(year, month - 1 + direction, 1);
    setSelectedMonth(newDate.toISOString().slice(0, 7));
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  const filteredAttendance = getFilteredAttendance();

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Header */}
      <div className="mb-8">
        <motion.h1 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-3xl font-bold text-gray-900 dark:text-white mb-2 flex items-center"
        >
          <Calendar className="w-8 h-8 mr-3 text-blue-600" />
          My Attendance
        </motion.h1>
        <p className="text-gray-600 dark:text-gray-400">
          View your attendance record and track your presence
        </p>
      </div>

      {/* Today's Status Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-8 border border-gray-200 dark:border-gray-700"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
              {getStatusIcon(attendanceData.todayStatus)}
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Today's Status</h3>
              <p className="text-gray-600 dark:text-gray-400">
                {new Date().toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  month: 'long', 
                  day: 'numeric',
                  year: 'numeric'
                })}
              </p>
            </div>
          </div>
          <div>
            {attendanceData.todayStatus ? (
              <span className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(attendanceData.todayStatus)}`}>
                {attendanceData.todayStatus === 'half_day' ? 'Half Day' : 
                 attendanceData.todayStatus.charAt(0).toUpperCase() + attendanceData.todayStatus.slice(1)}
              </span>
            ) : (
              <span className="px-4 py-2 rounded-full text-sm font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                Not Marked
              </span>
            )}
          </div>
        </div>
      </motion.div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        {/* Month Navigation */}
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigateMonth(-1)}
            className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white min-w-[140px] text-center">
            {new Date(selectedMonth + '-01').toLocaleDateString('en-US', { 
              month: 'long', 
              year: 'numeric' 
            })}
          </h2>
          <button
            onClick={() => navigateMonth(1)}
            className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Filters and Export */}
        <div className="flex items-center space-x-4">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          >
            <option value="all">All Status</option>
            <option value="present">Present</option>
            <option value="absent">Absent</option>
            <option value="half_day">Half Day</option>
          </select>
          
          <button
            onClick={exportAttendance}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Monthly Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8"
      >
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Present Days</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {attendanceData.monthlyStats.presentDays}
              </p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Half Days</p>
              <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                {attendanceData.monthlyStats.halfDays}
              </p>
            </div>
            <Clock className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Absent Days</p>
              <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                {attendanceData.monthlyStats.absentDays}
              </p>
            </div>
            <XCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Attendance %</p>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {attendanceData.monthlyStats.attendancePercentage}%
              </p>
            </div>
            <TrendingUp className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          </div>
        </div>
      </motion.div>

      {/* Attendance Records */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700"
      >
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Attendance Records ({filteredAttendance.length})
          </h3>
        </div>

        <div className="overflow-x-auto">
          {filteredAttendance.length > 0 ? (
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Check In
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Check Out
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Hours
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Notes
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredAttendance.map((record) => (
                  <tr key={record.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(record.status)}
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {formatShortDate(record.date)}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {new Date(record.date).toLocaleDateString('en-US', { weekday: 'short' })}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(record.status)}`}>
                        {record.status === 'half_day' ? 'Half Day' : 
                         record.status.charAt(0).toUpperCase() + record.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {record.checkInTime || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {record.checkOutTime || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {record.hoursWorked || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white max-w-xs truncate">
                      {record.notes || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-12">
              <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No Attendance Records
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                {filterStatus === 'all' 
                  ? 'No attendance records found for this month.'
                  : `No ${filterStatus} records found for this month.`
                }
              </p>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default TeacherMyAttendance;